import React, { useCallback, useMemo } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useMainEntityName, useProductEntity, useRetailerId, useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { formatMetric } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useStacklineTheme } from '@stackline/ui';
import { KEYWORD_FILTER_TO_METRIC_TYPE } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import { KeywordFilter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types';
import {
  ContentSubTabs,
  createProductColumn,
  DEFAULT_KEYWORD_POPULARITY_THRESHOLD,
  DEFAULT_KEYWORD_PRODUCTIVITY_THRESHOLD,
  DEFAULT_KEYWORD_USAGE_THRESHOLD
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/utils';
import { ContentSplineChart } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/ContentSplineChart';
import { KeywordsGaugeCards } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/KeywordsGaugeCards';
import { ProductLevelKeywordPerformanceDetails } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/ProductLevelKeywordPerformanceDetails';
import { ContentTableGridV3 } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/ContentTableGridV3';
import { useContentService } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useContentService';
import { ContentMetricType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { useContentAnalyticStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/stores/contentAnalyticStore';
import KeywordOppotunityTable from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Keywords/KeywordOppotunityTable';
import {
  DefaultColumnDefinitions,
  getSplineChartTitle,
  getWeekRecordMetricPropertyName
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/helpers/contentKeywordUtils';
import { useKeywordSubtitleV3 } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useKeywordSubtitleV3';
import { useKeywordPerformanceFilterStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/stores/KeywordPerformanceFilterStore';
import { getLastWeek, getLastWeekPreviousYear } from 'src/utils/dateformatting';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import { KeywordProductivityType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/ContentRequests/ContentRequestBuilder';
import { ContentSectionId } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/ContentSectionId';
import KeywordPerformanceFilterModal from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Modals/KeywordPerformanceFilterModal';
import {
  FilterLink,
  useFilterRendering
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useFilterRendering';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';
import { ChartContextProvider } from 'src/utils/chartContext/ChartContextGroupContext';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';

const ContentKeywordsV3Page = () => {
  const {
    getContentScoresByWeek,
    getContentScoresByProduct,
    getKeywordPerformanceScoreSummary,
    getProductLevelKeywordPerformanceDetails
  } = useContentService();
  const allWeekIdsByRetailerId = useAppSelector((state) => state.allWeekIdsByRetailerId);
  const retailerId = useRetailerId();
  const weekIds = allWeekIdsByRetailerId[retailerId];

  const { getSplineChartSubtitle } = useKeywordSubtitleV3();
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const { isProductEntity } = useProductEntity();
  const entityName = useMainEntityName();
  const { usageBelow70, productivityBelow70, popularityBelow70, openModal, getActiveFilter } =
    useKeywordPerformanceFilterStore();

  const activeFilters = useMemo(
    () => getActiveFilter(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getActiveFilter, usageBelow70, productivityBelow70, popularityBelow70]
  );
  const { renderFilters } = useFilterRendering(activeFilters);
  const { keywordFilter } = useContentAnalyticStore();

  const theme = useStacklineTheme();

  const getSubtitle = useCallback(() => {
    const comparisonPeriodDisplayedName = comparisonTimePeriod.shortDisplayName.toLowerCase();

    if (activeFilters.length > 0) {
      return (
        <Text variant="body1">
          The table displays the keyword performance metrics for each product last week with low{' '}
          <span>{renderFilters(activeFilters)}</span> performance compared to the{' '}
          {comparisonPeriodDisplayedName.toLowerCase()}.{' '}
          <FilterLink clickable onClick={openModal}>
            Edit {activeFilters.length === 1 ? 'Filter' : `${activeFilters.length} Filters`}
          </FilterLink>
          .
        </Text>
      );
    }

    return `The table displays the keyword performance metrics for each product last week compared to the ${comparisonPeriodDisplayedName}.`;
  }, [activeFilters, comparisonTimePeriod.shortDisplayName, renderFilters, openModal]);

  const keywordProductivityType = useMemo(() => {
    switch (keywordFilter) {
      case KeywordFilter.Productivity:
        return KeywordProductivityType.Productive;
      case KeywordFilter.UnProductiveKeywords:
        return KeywordProductivityType.Unproductive;
      default:
        return undefined;
    }
  }, [keywordFilter]);

  return (
    <BasePageContainer sx={{ display: 'flex', flexDirection: 'column', gap: `${BEACON_CHART_SPACING}px` }}>
      <KeywordsGaugeCards
        title="Keyword Performance"
        predefinedColor={theme.colors.primary}
        queryFn={() => getKeywordPerformanceScoreSummary(mainTimePeriod, comparisonTimePeriod)}
        renderSubtitle={() => {
          if (isProductEntity) {
            return `The metrics below display the keyword usage, productivity, and popularity for this product. The data represents the last week period compared to the ${comparisonTimePeriod.shortDisplayName.toLowerCase()}.`;
          } else {
            return `The metrics below display the keyword usage, productivity, and popularity for ${entityName}. The data represents the last week period compared to the ${comparisonTimePeriod.shortDisplayName.toLowerCase()}.`;
          }
        }}
      />

      <ChartContextProvider
        group={CHART_CONTEXT_GROUPS.BEACON_CONTENT_KEYWORDS_SPLINE.name}
        chartName={CHART_NAMES.BEACON_CONTENT_KEYWORDS_SPLINE_CHART}
      >
        <ContentSplineChart
          title={getSplineChartTitle(isProductEntity, keywordFilter)}
          queryFn={() =>
            getContentScoresByWeek({
              contentMetricType: KEYWORD_FILTER_TO_METRIC_TYPE[keywordFilter],
              mainTimePeriod,
              comparisonTimePeriod,
              recordProperty: getWeekRecordMetricPropertyName(keywordFilter),
              keywordProductivityType
            })
          }
          renderSubtitle={(score: string, change: number, averageKeywordCount?: number) => {
            if (keywordFilter === KeywordFilter.Popularity) {
              const cleanedScore = score.replace(/,/g, '').split('.')[0];
              const numericScore = Number(cleanedScore);
              return getSplineChartSubtitle({
                formattedAverage: formatMetric(numericScore, METRICTYPE.VOLUME, {
                  decimalPlaces: 1,
                  showFullValue: false
                }),
                keywordFilter,
                formattedPercentChange: formatMetric(Math.abs(change), METRICTYPE.PERCENT, { decimalPlaces: 2 }),
                direction: change > 0 ? 'higher' : 'lower',
                isProductEntity,
                keywordsUsed: formatMetric(averageKeywordCount, METRICTYPE.DECIMAL, {
                  decimalPlaces: isProductEntity ? 0 : 1
                })
              });
            }

            return getSplineChartSubtitle({
              formattedAverage: score,
              keywordFilter,
              formattedPercentChange: formatMetric(Math.abs(change), METRICTYPE.PERCENT, { decimalPlaces: 2 }),
              direction: change > 0 ? 'higher' : 'lower',
              isProductEntity
            });
          }}
          legendDecimalPlaces={keywordFilter === KeywordFilter.Popularity ? 0 : 2}
          metricType={METRICTYPE.DECIMAL}
        />
      </ChartContextProvider>

      {isProductEntity && <KeywordOppotunityTable />}

      {isProductEntity && (
        <ProductLevelKeywordPerformanceDetails
          queryFn={() => getProductLevelKeywordPerformanceDetails(mainTimePeriod)}
          keywordFilter={keywordFilter}
          renderSubtitle={() => {
            return 'This section displays the keyword performance metrics for every individual element last week.';
          }}
        />
      )}

      {!isProductEntity && (
        <div id={ContentSectionId.TableGrid} key={`${ContentSectionId.TableGrid}}`}>
          <ContentTableGridV3
            queryKeyOverride={`content-table-grid-keyword-performance-by-product-${usageBelow70}-${popularityBelow70}-${productivityBelow70}`}
            title="Keyword Performance by Product"
            mode="table"
            productColumn={createProductColumn('562px')}
            columnDefinitions={DefaultColumnDefinitions}
            queryFn={(page: number) =>
              getContentScoresByProduct(
                ContentMetricType.KeywordPerformanceScore,
                {
                  startWeek: getLastWeek(weekIds),
                  endWeek: getLastWeek(weekIds)
                },
                {
                  startWeek: getLastWeekPreviousYear(weekIds),
                  endWeek: getLastWeekPreviousYear(weekIds)
                },
                [],
                {
                  filterTitleInaccuracies: false,
                  filterBulletPointsInaccuracies: false,
                  filterImageInaccuracies: false,
                  filterDescriptionInaccuracies: false,
                  filterVideoInaccuracies: false,
                  minimumRequiredKeywordUsageScore: usageBelow70 ? DEFAULT_KEYWORD_USAGE_THRESHOLD : undefined,
                  minimumRequiredKeywordProductivityScore: productivityBelow70
                    ? DEFAULT_KEYWORD_PRODUCTIVITY_THRESHOLD
                    : undefined,
                  minimumRequiredKeywordPopularityScore: popularityBelow70
                    ? DEFAULT_KEYWORD_POPULARITY_THRESHOLD
                    : undefined
                },
                page
              )
            }
            tableSx={{
              minWidth: 650,
              borderCollapse: 'unset',
              '& tbody .MuiTableCell-root': {
                padding: '12px 72px',
                height: '60px',
                paddingRight: '0px'
              },
              '& thead .MuiTableCell-root': {
                padding: '0px 0px 16px 72px'
              },
              '& tbody td': {
                borderBottom: '1px solid #f0f0f0' // Move border bottom here
              },
              '& thead th': {
                border: 'none' // Explicitly remove borders from header cells
              },
              '& thead th:first-of-type': {
                padding: '0px 0px 16px 16px'
              },
              '& tbody td:first-of-type': {
                borderLeft: '1px solid #f0f0f0',
                padding: '16px 0px 16px 16px'
              },
              '& tbody td:last-child': {
                borderRight: '1px solid #f0f0f0'
              },
              '& tbody tr:first-of-type td': {
                borderTop: '1px solid #f0f0f0'
              },
              '& tbody tr:first-of-type td:first-of-type': {
                borderTopLeftRadius: '8px'
              },
              '& tbody tr:first-of-type td:last-of-type': {
                borderTopRightRadius: '8px'
              },
              '& tbody tr:first-of-type td:last-child': {
                borderTopRightRadius: '8px'
              },
              '& tbody tr:last-child td:first-of-type': {
                borderBottomLeftRadius: '8px'
              },
              '& tbody tr:last-child td:last-child': {
                borderBottomRightRadius: '8px'
              },
              '& thead .MuiTableCell-root span': {
                fontFamily: 'Roboto',
                fontSize: '12px',
                fontWeight: 700,
                lineHeight: '20px',
                letterSpacing: '1.2px',
                textAlign: 'left',
                textUnderlinePosition: 'from-font',
                textDecorationSkipInk: 'none',
                color: colors.darkBlue,
                textTransform: 'uppercase'
              }
            }}
            subtitle={getSubtitle()}
            filterModal={<KeywordPerformanceFilterModal />}
            setOpen={openModal}
            isFilterActive={activeFilters.length > 0}
            exportType={ContentSubTabs.contentKeywords}
          />
        </div>
      )}
    </BasePageContainer>
  );
};

export default ContentKeywordsV3Page;
